import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Drawer, IconButton, List, ListItem, ListItemIcon,
  ListItemText, Toolbar, Typography, Avatar, Menu, MenuItem, Divider,
  Badge, Chip, useTheme, Breadcrumbs, Link as MuiLink
} from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import {
  Menu as MenuIcon, Dashboard, ConfirmationNumber,
  People, Category, AccountCircle, Logout, Business,
  TrendingUp, Assessment, HourglassEmpty
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 260;

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isGuest = user?.role === 'guest';

  let menuItems = [];

  if (!isGuest) {
    menuItems.push({ text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' });
    menuItems.push({ text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' });
    menuItems.push({ text: 'KC-Statistik', icon: <Assessment />, path: '/kc-overview' });
    menuItems.push({ text: 'KC-Verwaltung', icon: <Business />, path: '/kc-management' });
    menuItems.push({ text: 'Kategorien', icon: <Category />, path: '/categories' });

    if (user?.role === 'admin') {
      menuItems.push({ text: 'Stunden-Auswertung', icon: <HourglassEmpty />, path: '/reports' });
      menuItems.push({ text: 'Benutzer', icon: <People />, path: '/users' });
    }
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{
        background: '#ffffff',
        minHeight: '90px !important',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: 'none',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '3%',
          right: '3%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #CC0000 30%, #CC0000 70%, transparent)',
          boxShadow: '0 0 8px rgba(204, 0, 0, 0.4), 0 0 20px rgba(204, 0, 0, 0.15)',
        },
        py: 1.5
      }}>
        <img
          src="/soltegra_logo.png"
          alt="MBI-Montagen"
          style={{ maxWidth: 140, height: 'auto', marginBottom: 4 }}
        />
        <Typography
          variant="caption"
          sx={{
            color: '#888',
            fontWeight: 500,
            fontSize: '0.65rem',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}
        >
          PLC Ticket Portal
        </Typography>
      </Toolbar>
      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            px: 1,
            letterSpacing: '0.5px'
          }}
        >
          HAUPTMENÜ
        </Typography>
        <List sx={{ mt: 1 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <ListItem
                button
                key={item.text}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                selected={isSelected}
                sx={{
                  mb: 0.5,
                  mx: 0.5,
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected 
                      ? theme.palette.primary.dark 
                      : 'rgba(204, 0, 0, 0.08)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: isSelected ? '#ffffff' : theme.palette.text.secondary,
                  minWidth: '40px'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? '#ffffff' : theme.palette.text.primary
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: theme.palette.text.primary
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" />}
              sx={{
                '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' },
                '& a': { textDecoration: 'none' }
              }}
            >
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/dashboard')}
                sx={{
                  color: location.pathname === '/dashboard' ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: location.pathname === '/dashboard' ? 600 : 400,
                  cursor: 'pointer',
                  '&:hover': { color: theme.palette.primary.main }
                }}
              >
                Dashboard
              </MuiLink>
              {location.pathname === '/tickets' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Tickets
                </Typography>
              )}
              {location.pathname.startsWith('/tickets/') && location.pathname !== '/tickets/new' && location.pathname !== '/tickets/import' && location.pathname !== '/tickets/import-pptx' && (
                [
                  <MuiLink
                    key="tickets"
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/tickets')}
                    sx={{ color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }}
                  >
                    Tickets
                  </MuiLink>,
                  <Typography key="detail" variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Ticket #{location.pathname.split('/').pop()}
                  </Typography>
                ]
              )}
              {location.pathname === '/tickets/new' && (
                [
                  <MuiLink
                    key="tickets"
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/tickets')}
                    sx={{ color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }}
                  >
                    Tickets
                  </MuiLink>,
                  <Typography key="new" variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Neues Ticket
                  </Typography>
                ]
              )}
              {location.pathname === '/tickets/import' && (
                [
                  <MuiLink
                    key="tickets"
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/tickets')}
                    sx={{ color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }}
                  >
                    Tickets
                  </MuiLink>,
                  <Typography key="import" variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Tickets importieren
                  </Typography>
                ]
              )}
              {location.pathname === '/tickets/import-pptx' && (
                [
                  <MuiLink
                    key="tickets"
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/tickets')}
                    sx={{ color: theme.palette.text.secondary, cursor: 'pointer', '&:hover': { color: theme.palette.primary.main } }}
                  >
                    Tickets
                  </MuiLink>,
                  <Typography key="pptx" variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    PPTX Import
                  </Typography>
                ]
              )}
              {location.pathname === '/kc-overview' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  KC-Statistik
                </Typography>
              )}
              {location.pathname === '/kc-management' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  KC-Verwaltung
                </Typography>
              )}
              {location.pathname === '/categories' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Kategorien
                </Typography>
              )}
              {location.pathname === '/users' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Benutzerverwaltung
                </Typography>
              )}
              {location.pathname === '/profile' && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Mein Profil
                </Typography>
              )}
            </Breadcrumbs>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: theme.palette.text.secondary,
                letterSpacing: '0.5px',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {new Date().toLocaleDateString('de-DE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0.5,
              '&:hover': {
                transform: 'scale(1.05)',
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40,
                background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)',
                fontWeight: 600,
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                mt: 1.5,
                borderRadius: '12px',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || user?.role}
              </Typography>
            </Box>
            <MenuItem 
              onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}
              sx={{ 
                mx: 1, 
                my: 0.5, 
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(204, 0, 0, 0.08)',
                }
              }}
            >
              <AccountCircle sx={{ mr: 1.5, color: theme.palette.primary.main }} /> 
              Mein Profil
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem 
              onClick={handleLogout}
              sx={{ 
                mx: 1, 
                my: 0.5, 
                borderRadius: '8px',
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                }
              }}
            >
              <Logout sx={{ mr: 1.5 }} /> 
              Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100vw',
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Box sx={{ 
          animation: 'fadeInUp 0.5s ease-in-out',
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}>
          {isGuest ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center',
              gap: 2
            }}>
              <HourglassEmpty sx={{ fontSize: 80, color: theme.palette.warning.main, opacity: 0.6 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Konto noch nicht freigeschaltet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                Ihr Konto wurde erfolgreich erstellt, aber ein Administrator muss es erst freischalten,
                bevor Sie auf das Portal zugreifen können.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bitte wenden Sie sich an Ihren Administrator.
              </Typography>
            </Box>
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;